import { boot } from "quasar/wrappers"
import { Subscription } from "rxjs"
import { getChangesDB, getDB } from "src/db/databases"
import { useAuthStore } from "src/stores/authStore"
import {
  cacheDeleteIssueWithOpenCorrectiveWork,
  cacheDeleteItemWithWorkOrders,
  cacheInsertIssueWithOpenCorrectiveWork,
  cacheInsertItemWithWorkOrders,
  cacheItemWithIssues,
  cacheUpdateIssueWithOpenCorrectiveWork,
  cacheUpdateItemWithWorkOrders,
  getAttributeValueIDFromValue,
  useCacheStore,
} from "src/stores/cacheStore"
import { useUnsubmittedWorkStore } from "src/stores/unsubmittedWork"

export default boot(async () => {
  const authStore = useAuthStore()

  // No need to build caches if there is no data
  if (authStore.selectedWorkspace) {
    await syncDBCache()
    await syncUnsubmittedWork()
  }
})

let dbSyncSubscription: Subscription
let dbIssueItemSubscription: Subscription
let dbWorkOrderRegularItemSubscription: Subscription
let dbCorrectiveWorkItemSubscription: Subscription
let dbAttributeValuesSubscription: Subscription
let dbUnsubmittedWorkSubscription: Subscription

export async function syncDBCache() {
  dbSyncSubscription?.unsubscribe()
  dbIssueItemSubscription?.unsubscribe()
  dbWorkOrderRegularItemSubscription?.unsubscribe()
  dbCorrectiveWorkItemSubscription?.unsubscribe()
  dbAttributeValuesSubscription?.unsubscribe()

  const db = await getDB()
  const cacheStore = useCacheStore()
  await cacheStore.rebuildCache()

  const attributeValuesCollections = new Set([
    "attribute_char_value",
    "attribute_numeric_value",
    "attribute_select_value",
    "attribute_temporal_value",
  ])

  dbIssueItemSubscription = db.issues_on_item.insert$.subscribe((i) =>
    cacheItemWithIssues([i.documentData], cacheStore.itemIssues)
  )

  dbWorkOrderRegularItemSubscription = db.work_order_regular_item.$.subscribe((i) => {
    switch (i.operation) {
      case "INSERT":
        cacheInsertItemWithWorkOrders([i.documentData], cacheStore.itemWorkOrders)
        break

      case "UPDATE":
        cacheUpdateItemWithWorkOrders(
          [i.previousDocumentData],
          [i.documentData],
          cacheStore.itemWorkOrders
        )
        break

      case "DELETE":
        cacheDeleteItemWithWorkOrders([i.documentData], cacheStore.itemWorkOrders)
        break
    }
  })

  dbCorrectiveWorkItemSubscription = db.corrective_work_on_item.$.subscribe((i) => {
    switch (i.operation) {
      case "INSERT":
        cacheInsertIssueWithOpenCorrectiveWork(
          [i.documentData],
          cacheStore.issueWithOpenCorrectiveWork
        )
        break

      case "UPDATE":
        cacheUpdateIssueWithOpenCorrectiveWork(
          i.previousDocumentData,
          i.documentData,
          cacheStore.issueWithOpenCorrectiveWork
        )
        break

      case "DELETE":
        cacheDeleteIssueWithOpenCorrectiveWork(
          i.documentData,
          cacheStore.issueWithOpenCorrectiveWork
        )
        break
    }
  })

  dbAttributeValuesSubscription = db.$.subscribe((event) => {
    // Skip NON attribute values because we want to handle them in the generic listener
    if (!event.collectionName || !attributeValuesCollections.has(event.collectionName)) return

    const id = getAttributeValueIDFromValue(event.documentData)

    switch (event.operation) {
      case "INSERT":
      case "UPDATE": {
        const cacheCollection = cacheStore.getCollection(event.collectionName)
        if (cacheCollection) {
          // Clean up from RxDB
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _attachments, _deleted, _meta, _rev, ...docData } = event.documentData
          cacheCollection.set(id, docData)
        }
        break
      }
      case "DELETE":
        const cacheCollection = cacheStore.getCollection(event.collectionName)
        if (cacheCollection) {
          cacheCollection.delete(id)
        }
        break
    }
  })

  dbSyncSubscription = db.$.subscribe((event) => {
    // Skip attribute values because we want to have a dedicated listener for them
    if (!event.collectionName || attributeValuesCollections.has(event.collectionName)) return

    switch (event.operation) {
      case "INSERT":
      case "UPDATE": {
        const cacheCollection = cacheStore.getCollection(event.collectionName)
        if (cacheCollection && event.documentData?.id) {
          // Clean up from RxDB
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _attachments, _deleted, _meta, _rev, ...docData } = event.documentData

          cacheCollection.set(event.documentData.id, docData)
        }
        break
      }
      case "DELETE":
        const cacheCollection = cacheStore.getCollection(event.collectionName)
        if (cacheCollection) {
          cacheCollection.delete(event.documentData.id)
        }
        break
    }
  })
}

export async function syncUnsubmittedWork() {
  dbUnsubmittedWorkSubscription?.unsubscribe()

  const cdb = await getChangesDB()
  const unsubmittedWorkStore = useUnsubmittedWorkStore()

  dbUnsubmittedWorkSubscription = cdb.$.subscribe((event) => {
    switch (event.operation) {
      case "INSERT":
      case "DELETE": {
        unsubmittedWorkStore.updateHasUnsubmittedWork()
        break
      }
    }
  })
}
